//import { captureException } from '@sentry/react';
import { isEmpty } from 'lodash';
import memoize from 'memoizee';
import { useCallback, useEffect, useState } from 'react';
import Button from '../../components/Button/Button';
import InfoDialogUI from '../../components/InfoDialog/InfoDialogUI';
import { ONE_MINUTE } from '../../constants/constants';
import {
  apiGetHdriLight,
  apiGetHdriList,
  apiGetMaterials,
  apiGetMaterialsV2,
  apiGetPropsV2,
  apiGetTextureList,
  apiShareMultipleModelType,
} from '../../helpers/api';
import { fetchAllPages } from '../../helpers/apiHelper';
import { apiErrorHandler } from '../../helpers/axiosHelper';
import { errorToast } from '../../helpers/toastHelper';
import { ItemSelectListUI } from '../OrgSection/ItemSelectListUI';

const mockRequestAccessData = {
  texture: [
    17592, 16364, 16365, 16366, 16367, 16368, 16369, 16370, 16371, 16372, 16373, 16375, 16376,
    16377, 16378, 16379, 16380, 16381, 16384, 16385,
  ],
  prop: [
    4714, 159, 413, 343, 401, 405, 455, 95, 1091, 3046, 105, 22, 923, 3043, 2062, 43, 1947, 36, 18,
    431,
  ],
  material: [],
  hdri: [],
};

export const NotificationSharePropsDialog = ({ show, onClose, details, onSuccess, type }) => {
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [props, setProps] = useState([]);
  const [textures, setTextures] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [hdri, setHdri] = useState([]);

  const isRequest = type === TYPE.REQUEST;

  const memoizedGetData = useCallback(
    memoize(
      async (data) => {
        setLoading(true);
        let { texture = [], product = [], material = [], hdri = [] } = data;

        const normalizeData = (d) => {
          if (Array.isArray(d)) return d;
          if (typeof d === 'object') {
            return Object.keys(d);
          }
          return [];
        };

        texture = normalizeData(texture);
        product = normalizeData(product);
        material = normalizeData(material);
        hdri = normalizeData(hdri);

        const fetchDataPromises = [];

        const fetchData = (idList, api, setter, isComaSeparated = false, acceptIdList = false) => {
          if (!idList.length) return;

          fetchDataPromises.push(async () => {
            let query;

            if (isComaSeparated) {
              query = `id=${idList.join(',')}`;
            } else if (!acceptIdList) {
              query = idList.map((id) => `id=${id}`).join('&');
            }
            const result = await fetchAllPages((page) => {
              const reuqestData = { query, page };
              if (acceptIdList) {
                reuqestData.ids = idList;
              }
              return api(reuqestData);
            });

            if (Array.isArray(result?.results)) {
              setter(result.results);
            }
          });
        };

        fetchData(texture, apiGetTextureList, setTextures, true);
        fetchData(product, apiGetPropsV2, setProps);
        fetchData(material, apiGetMaterialsV2, setMaterials);
        fetchData(hdri, apiGetHdriList, setHdri, false, true);

        await Promise.all(fetchDataPromises.map((cb) => cb()));

        setLoading(false);
      },
      { length: 1, maxAge: 10 * ONE_MINUTE }
    ),
    []
  );

  useEffect(() => {
    memoizedGetData(details.request_access_data || details.shared_data);
    // memoizedGetData(mockRequestAccessData);
  }, [details.request_access_data]);

  const confirmAccept = async () => {
    const sharedTo = details?.request_access_data?.shared_to;
    const sharedToOrg = details?.request_access_data?.shared_to_org;
    if (!sharedTo && !sharedToOrg) {
      errorToast('Request configuration is not correct.');
      //captureException('Notification -> permissions request does not have `sharedTo` or `sharedToOrg` parameters');
      return;
    }

    const payload = new FormData();

    if (!isEmpty(sharedToOrg)) {
      payload.append('shared_to_org', sharedToOrg.join(','));
    }

    if (!isEmpty(sharedTo)) {
      payload.append('shared_to', sharedTo.join(','));
    }

    setLoading(true);

    const mapFunc = (type) => (key) => {
      const [selectedType, id] = key.split('__');
      return selectedType === type ? id : null;
    };

    const selectedProps = selected.map(mapFunc('prop')).filter(Boolean).join(',');
    const selectedTextures = selected.map(mapFunc('texture')).filter(Boolean).join(',');
    const selectedMaterials = selected.map(mapFunc('material')).filter(Boolean).join(',');
    const selectedHdri = selected.map(mapFunc('hdri')).filter(Boolean).join(',');

    if (!isEmpty(selectedProps)) payload.append('product', selectedProps);
    if (!isEmpty(selectedTextures)) payload.append('texture', selectedTextures);
    if (!isEmpty(selectedMaterials)) payload.append('material', selectedMaterials);
    if (!isEmpty(selectedHdri)) payload.append('hdri', selectedHdri);

    const result = await apiShareMultipleModelType(payload);
    setLoading(false);

    if (!result?.status) {
      apiErrorHandler(result);
    } else {
      onSuccess?.();
    }
  };

  const mapFunc = (type) => (item) => ({
    id: `${type}__${item.id}`,
    title: item.name,
    image: item.thumbnail,
  });

  const listItems = [];

  const addDataToList = (list, title, type) => {
    if (!list.length) return;

    listItems.push({ isSectionTitle: true, title, id: title }, ...list.map(mapFunc(type)));
  };

  addDataToList(props, '3d Models :', 'prop');
  addDataToList(textures, 'Textures :', 'texture');
  addDataToList(materials, 'Materials :', 'material');
  addDataToList(hdri, 'HDRI :', 'hdri');

  const hasSelected = !isEmpty(selected);

  const title = isRequest ? 'Requested files' : 'Shared files';
  const description = isRequest
    ? `Share selected with the ${details?.placeholder_data?.OrgName || 'organization'}.`
    : 'Click on shared file for preview';

  return (
    <InfoDialogUI show={show} onClose={onClose} title={title} description={description}>
      <ItemSelectListUI
        className="mb-4"
        items={listItems}
        selected={selected}
        onChange={(list) => setSelected(list)}
        loading={loading}
        hasData={true}
        variant={isRequest ? 'checkbox' : 'default'}
        minHeight={431}
        placeholder
      />

      {isRequest && (
        <Button
          color="warning"
          fullWidth
          size="xlg"
          disabled={loading || !hasSelected}
          onClick={confirmAccept}>
          Share & Grant access
        </Button>
      )}
    </InfoDialogUI>
  );
};

const TYPE = {
  REQUEST: 'REQUEST',
  SHARED_DATA: 'SHARED_DATA',
};

NotificationSharePropsDialog.TYPE = TYPE;
